export interface CellularPayPeriod {
  start: string
  end: string
  amountUsd: number
  // Set only on a back-bill's extra per-controller line, e.g.
  // "Cellular data MS-001, Jun 15 2022 – Aug 20 2025 (3 years)".
  description?: string | null
}

// One controller a back-bill charges: which, when its cellular service expired, the
// years billed and the amount (PM, 2026-09-30). Shown beside the PM's wording.
export interface CellularBilledController {
  serial: string
  model?: string | null
  expiredOn?: string | null
  years: number
  /** Days billed after those years, up to the renewal (D57); 0 or absent when none. */
  partYearDays?: number | null
  amountUsd: number
  billedFrom?: string | null
  billedTo?: string | null
  forgivenYears?: number
  forgivenFrom?: string | null
  forgivenTo?: string | null
  forgivenUsd?: number
}

// A saved US bank account (ACH): the subscription's own, else the customer's default.
export interface CellularSavedBankAccount {
  paymentMethodId: string
  bankName?: string | null
  last4?: string | null
  // True when the cellular subscription renews on this bank account.
  renewsOn: boolean
}

// A bank payment on the shown bill. While it is on its way (or waiting for the
// test deposits) and inside its 7 days, the bill does not lock (`blocking` false) —
// only the first bank payment on a bill: a later one (`countsAsPaid` false) keeps
// the bill owed until its money lands.
export type CellularAchState = 'waiting_deposits' | 'processing' | 'failed' | 'expired'

// 'instant': bank login. 'microdeposits': typed numbers. 'automatic': a renewal charged
// to the bank account on file.
export type CellularAchKind = 'instant' | 'microdeposits' | 'automatic'

export interface CellularAchPayment {
  state: CellularAchState
  kind: CellularAchKind
  submittedAt?: string | null
  // Until when it counts as paid; null once failed.
  trustUntil?: string | null
  // Stripe's page to confirm the test deposits (owner only).
  verificationUrl?: string | null
  // False: an earlier bank payment on this bill was tried, so this one does not
  // count as paid while it clears.
  countsAsPaid?: boolean | null
  failedAt?: string | null
  failureReason?: string | null
}

export interface CellularSavedCard {
  paymentMethodId: string
  brand: string
  last4: string
  expMonth: number
  expYear: number
}

// Another unpaid cellular invoice behind the one shown. One subscription can owe two
// back-bills at once: its first one and one for controllers added to it later (D37).
export interface CellularOtherBill {
  invoiceId: string
  stripeSubscriptionId: string
  amountDueUsd: number
  dueDate?: string | null
  isBackbill: boolean
}

// Where a covered SIM is in collections. 'active' appears only per controller:
// the summary is null when every covered SIM is active.
export type CellularSimStage = 'active' | 'past_due' | 'suspended' | 'on_hold' | 'deactivated'

export interface CellularSimStatusController {
  serial?: string | null
  displayName?: string | null
  stage: CellularSimStage | string
  suspendedAt?: string | null
  shutOffOn?: string | null
  deactivatedAt?: string | null
}

// The most severe stage among the SIMs this bill covers, with its dates.
export interface CellularSimStatusSummary {
  stage: CellularSimStage | string
  suspendedAt?: string | null
  shutOffOn?: string | null
  deactivatedAt?: string | null
  controllers: CellularSimStatusController[]
}

export interface CellularPayPrompt {
  stripeSubscriptionId: string
  invoiceId: string
  invoiceStatus: string
  amountDueUsd: number
  currency: string
  dueDate?: string | null
  daysPastDue: number
  hostedInvoiceUrl?: string | null
  expiredOn?: string | null
  periods: CellularPayPeriod[]
  controllers: number
  // A back-bill's controllers, earliest expiry first; empty for a renewal.
  billedControllers?: CellularBilledController[]
  // What the subscription renews at on nextInvoiceDate (seats × yearly price).
  renewalAmountUsd?: number | null
  renewalSeats?: number | null
  // The years the two-year cap forgave, and their total (PM's Lovable design).
  forgivenPeriods?: CellularPayPeriod[]
  courtesyCreditUsd?: number
  nextInvoiceDate?: string | null
  savedCard?: CellularSavedCard | null
  // True only for the back-bill catch-up. An ordinary renewal is the billing page's job.
  isBackbill: boolean
  blocking: boolean
  gaveUp: boolean
  // An account with several cellular subscriptions owes one invoice per
  // subscription, and only the soonest-due one is shown here.
  otherUnpaidInvoices: number
  accountTotalDueUsd: number
  // The other unpaid bills in the order they come up. Absent from an older paywall.
  otherBills?: CellularOtherBill[] | null
  // False when this is the workspace owner's back-bill shown to someone who works
  // in their workspace: they cannot pay it, so the owner is named instead.
  viewerIsOwner?: boolean | null
  ownerName?: string | null
  ownerEmail?: string | null
  // Null when every covered SIM is active. Absent from an older paywall.
  simStatus?: CellularSimStatusSummary | null
  // False when every covered SIM is shut off for good: nothing can be paid.
  payable?: boolean | null
  // Whether paying by US bank account (ACH) is offered. Absent from an older paywall.
  achEnabled?: boolean | null
  savedBankAccount?: CellularSavedBankAccount | null
  achPayment?: CellularAchPayment | null
  // The paywall's own answers, read from its bill ledger (SHA-973 P2). Absent from
  // an older paywall, and null while its reader is off: then the CMS decides as
  // before (isLocked, initialView).
  // Whether the bill locks the CMS: the whole lock answer, not just the bill kind.
  locks?: boolean | null
  // Whether the owed dialog opens on its own.
  popUp?: boolean | null
  // The ledger's kind of the bill. Not read here yet.
  billKind?: string | null
  // SHA-973 P4d: the fresh start emailed this old bill (null when the prompt reader is off).
  freshStart?: boolean | null
}

export interface CellularPayResult {
  paid: boolean
  // The Stripe invoice status, or a PaymentIntent status such as 'requires_action'
  // when the card needs 3-D Secure authentication.
  status?: string | null
  message: string
  hostedInvoiceUrl?: string | null
  // The PaymentIntent secret for a card that needs 3-D Secure. Set when Stripe gave
  // the API one; when it is null the customer is sent to the hosted invoice page.
  paymentIntentClientSecret?: string | null
  // Bank payments only: 'processing' (on its way) or 'waiting_deposits'.
  achState?: CellularAchState | null
  // Typed bank numbers: Stripe's page to confirm the test deposits.
  verificationUrl?: string | null
  // Bank payments only: false when the bill stays owed until the money lands.
  achCountsAsPaid?: boolean | null
}

export interface PaymentFailure {
  message: string
  hostedInvoiceUrl?: string | null
  // The invoice's amount changed under the customer: offer to refresh it.
  amountChanged?: boolean
}

export type PromptView = 'owed' | 'payment' | 'giveUp' | 'closed'

// 'saved' / 'new': a card. 'savedBank': the bank account on file. 'bank': add one now
// (bank login, or typed numbers verified by Stripe's test deposits).
export type CardMethod = 'saved' | 'new'
export type BankMethod = 'savedBank' | 'bank'
export type PaymentMethodChoice = CardMethod | BankMethod

// A US bank account is paid through use-bank-payment.
export const isBankChoice = (method: PaymentMethodChoice): method is BankMethod =>
  method === 'savedBank' || method === 'bank'
